// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";

import React, { FC } from "react";

// eslint-disable-next-line spire/fenced-imports
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

const enum fields {
    headerText = "headerText",
    subHeaderText = "subHeaderText",
    imageUrl = "imageUrl",
    servicesList = "servicesList",
    imagePosition = "imagePosition",
    columnCount = "columnCount",
}

export interface ServiceShowcaseStyles {
    gridContainerStyles: InjectableCss;
    gridItemLeftHeaderImageStyles: InjectableCss;
    gridItemRightHeaderImageStyles: InjectableCss;
    gridItemHeaderImageStyles: InjectableCss;
    serviceContainerStyles: InjectableCss;
    serviceListStylesOne: InjectableCss;
    serviceListStylesTwo: InjectableCss;
    serviceListStylesThree: InjectableCss;
}

export const serviceShowcaseStyles: ServiceShowcaseStyles = {
    gridContainerStyles: {
        css: css`
            @media print {
                max-width: 100%;
            }
        `,
    },
    gridItemHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
            padding-left: 0px;
        `,
    },
    gridItemLeftHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
            padding-left: 0px;
            ${breakpointMediaQueries(baseTheme, [
                css``,
                css``,
                css`
                    padding-right: 30px;
                `,
                css`
                    padding-right: 30px;
                `,
                css`
                    padding-right: 30px;
                `,
            ])}
        `,
    },
    gridItemRightHeaderImageStyles: {
        css: css`
            padding-bottom: 0px;
            padding-left: 0px;
            ${breakpointMediaQueries(baseTheme, [
                css``,
                css``,
                css`
                    padding-left: 30px;
                `,
                css`
                    padding-left: 30px;
                `,
                css`
                    padding-left: 30px;
                `,
            ])}
        `,
    },
    serviceContainerStyles: {
        css: css`
            margin-top: 1em !important;
        `,
    },
    serviceListStylesOne: {
        css: css`
            ul li:after {
                content: " ";
                display: block;
                width: 0.6em;
                height: 1.1em;
                border: solid ${getColor("primary.main")};
                border-width: 0 0.2em 0.2em 0;
                position: absolute;
                left: 1em;
                top: 22%;
                margin-top: -0.2em;
                -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg);
                transform: rotate(45deg);
            }
            ul li {
                font-size: 15px;
                margin-bottom: 0.5em;
                list-style-type: none;
                padding: 0.25em 0 0 3em;
                position: relative;
                flex: 0 0 100%;
            }
            ul {
                list-style-type: none;
                display: flex;
                flex-wrap: wrap;
                padding: 0;
            }
        `,
    },
    serviceListStylesTwo: {
        css: css`
            ul li:after {
                content: " ";
                display: block;
                width: 0.6em;
                height: 1.1em;
                border: solid ${getColor("primary.main")};
                border-width: 0 0.2em 0.2em 0;
                position: absolute;
                left: 1em;
                top: 22%;
                margin-top: -0.2em;
                -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg);
                transform: rotate(45deg);
            }
            ul li {
                font-size: 15px;
                margin-bottom: 0.5em;
                list-style-type: none;
                padding: 0.25em 0 0 3em;
                position: relative;
                flex: 0 0 50%;
            }
            ul {
                list-style-type: none;
                display: flex;
                flex-wrap: wrap;
                padding: 0;
            }
        `,
    },
    serviceListStylesThree: {
        css: css`
            ul li:after {
                content: " ";
                display: block;
                width: 0.6em;
                height: 1.1em;
                border: solid ${getColor("primary.main")};
                border-width: 0 0.2em 0.2em 0;
                position: absolute;
                left: 1em;
                top: 22%;
                margin-top: -0.2em;
                -webkit-transform: rotate(45deg);
                -moz-transform: rotate(45deg);
                -o-transform: rotate(45deg);
                transform: rotate(45deg);
            }
            ul li {
                font-size: 15px;
                margin-bottom: 0.5em;
                list-style-type: none;
                padding: 0.25em 0 0 3em;
                position: relative;
                flex: 0 0 33%;
            }
            ul {
                list-style-type: none;
                display: flex;
                flex-wrap: wrap;
                padding: 0;
            }
            ${breakpointMediaQueries(baseTheme, [
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    ul li {
                        font-size: 15px;
                        margin-bottom: 0.5em;
                        list-style-type: none;
                        padding: 0.25em 0 0 3em;
                        position: relative;
                        flex: 0 0 50%;
                    }
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 0px;
                    margin-right: 0px;
                `,
                css`
                    margin-left: 51px;
                    margin-right: 51px;
                `,
            ])}
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headerText]: string;
        [fields.subHeaderText]: string;
        [fields.imageUrl]: string;
        [fields.servicesList]: string;
        [fields.imagePosition]: string;
        [fields.columnCount]: string;
    };
}

type Props = OwnProps;

const styles = serviceShowcaseStyles;
const ServiceShowcase: FC<Props> = ({ fields, ...otherProps }) => {
    let mobileLeftWrapStyle = {};
    let mobileRightWrapStyle = {};
    if (fields.imagePosition === "right") {
        mobileLeftWrapStyle = {
            css: css`
                ${breakpointMediaQueries(baseTheme, [
                    css``,
                    css``,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
            `,
        };
    }
    if (fields.imagePosition === "left") {
        mobileRightWrapStyle = {
            css: css`
                ${breakpointMediaQueries(baseTheme, [
                    css``,
                    css``,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                ])}
            `,
        };
    }
    return (
        <StyledWrapper data-test-selector="custom_content_service_showcase" className="content_showcase_wrapper">
            <GridContainer style={{ paddingTop: "30px", paddingBottom: "30px" }} {...styles.gridContainerStyles}>
                <GridItem
                    width={[12, 12, 12, 6, 6]}
                    {...styles.gridItemLeftHeaderImageStyles}
                    className="content_showcase_image_left_align"
                    {...mobileLeftWrapStyle}
                >
                    <LazyImage
                        src={fields.imageUrl}
                        altText={translate("Content Header Image")}
                        style={{ width: "94%", height: "auto" }}
                    />
                </GridItem>
                <GridItem
                    width={[12, 12, 12, 6, 6]}
                    {...styles.serviceContainerStyles}
                    className="content_showcase_list_parent"
                >
                    <GridContainer {...styles.gridItemHeaderImageStyles}>
                        <GridItem width={12} {...styles.gridItemHeaderImageStyles} className="content_header_text">
                            <Typography variant="h2" id={fields.headerText} style={{ marginBottom: "0em", fontSize: "24px" }}>
                                {fields.headerText}
                            </Typography>
                        </GridItem>
                        <GridItem width={12} {...styles.gridItemHeaderImageStyles} className="content_subheader_text">
                            <Typography variant="h3" style={{ fontWeight: 600, paddingLeft: "0px", fontSize: "18.72px" }}>
                                {fields.subHeaderText}
                            </Typography>
                        </GridItem>
                        {fields.servicesList && fields.servicesList !== "" && fields.columnCount === "1" && (
                            <GridItem width={12} {...styles.serviceListStylesOne}>
                                <ul className="content_one_column_ul">
                                    {fields.servicesList.split("|").map((element, index) => {
                                        return <li key={element}>{element}</li>;
                                    })}
                                </ul>
                            </GridItem>
                        )}
                        {fields.servicesList && fields.servicesList !== "" && fields.columnCount === "2" && (
                            <GridItem width={12} {...styles.serviceListStylesTwo}>
                                <ul className="content_two_column_ul">
                                    {fields.servicesList.split("|").map((element, index) => {
                                        return <li key={element}>{element}</li>;
                                    })}
                                </ul>
                            </GridItem>
                        )}
                        {fields.servicesList && fields.servicesList !== "" && fields.columnCount === "3" && (
                            <GridItem width={12} {...styles.serviceListStylesThree}>
                                <ul className="content_three_column_ul">
                                    {fields.servicesList.split("|").map((element, index) => {
                                        return <li key={element}>{element}</li>;
                                    })}
                                </ul>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                <GridItem
                    width={[0, 0, 6, 6, 6]}
                    {...styles.gridItemRightHeaderImageStyles}
                    className="content_showcase_image_right_align"
                    {...mobileRightWrapStyle}
                >
                    <LazyImage
                        src={fields.imageUrl}
                        altText={translate("Content Header Image")}
                        style={{ width: "94%", height: "auto" }}
                    />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ServiceShowcase,
    definition: {
        displayName: "Service Showcase",
        icon: "Grid",
        fieldDefinitions: [
            {
                name: fields.headerText,
                displayName: "Services header Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The services header text.",
                placeholder: "Services header Text Placeholder",
                isRequired: true,
            },
            {
                name: fields.subHeaderText,
                displayName: "Services subheader text",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "The sub-heading text for the service showcase widget.",
                placeholder: "Sub-heading text placeholder.",
                isRequired: false,
            },
            {
                name: fields.imageUrl,
                displayName: "Services Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.servicesList,
                displayName: "Services List",
                editorTemplate: "MultilineTextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Pipe-delimited list of services.",
                placeholder: "List item 1 | List item 2",
                isRequired: false,
            },
            {
                name: fields.imagePosition,
                displayName: "Image Position",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Right",
                        value: "right",
                    },
                    {
                        displayName: "Left",
                        value: "left",
                    },
                ],
                defaultValue: "left",
                fieldType: "General",
            },
            {
                name: fields.columnCount,
                displayName: "Services List Column Count",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "1",
                        value: "1",
                    },
                    {
                        displayName: "2",
                        value: "2",
                    },
                    {
                        displayName: "3",
                        value: "3",
                    },
                ],
                defaultValue: "1",
                fieldType: "General",
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
